// frontend/src/pages/OrdenesCompraPage.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { FiEdit, FiTrash2, FiPackage } from 'react-icons/fi';
import { ordenCompraService } from '../services/onebaseService';
import VerEditarOrdenCompraModal from '../components/VerEditarOrdenCompraModal';
import RecepcionarCompraModal from '../components/RecepcionarCompraModal';
import Loading from '../components/Loading';
import './OrdenesCompraPage.css';

const OrdenesCompraPage = () => {
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrden, setExpandedOrden] = useState(null);

  // Estados para filtros
  const [filtroRecurso, setFiltroRecurso] = useState('');
  const [filtroProveedor, setFiltroProveedor] = useState('');
  const [filtroProyecto, setFiltroProyecto] = useState('');
  const [filtroNumeroOC, setFiltroNumeroOC] = useState('');

  // Estados para modal de edición
  const [showVerEditarOC, setShowVerEditarOC] = useState(false);
  const [ordenCompraParaVer, setOrdenCompraParaVer] = useState(null);

  // Estados para modal de recepción
  const [showRecepcionModal, setShowRecepcionModal] = useState(false);
  const [ordenCompraParaRecepcionar, setOrdenCompraParaRecepcionar] = useState(null);

  useEffect(() => {
    cargarOrdenes();
  }, []);

  const cargarOrdenes = async () => {
    try {
      setLoading(true);
      // Derivar URL base para OneBase desde la baseURL actual de `api`
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/content\/?$/i, '/onebase');

      const response = await api.get(`${API_BASE_URL}/ordenes-compra/`);
      setOrdenes(response.data.results || response.data);
    } catch (error) {
      console.error('Error al cargar órdenes de compra:', error);
      toast.error('Error al cargar las órdenes de compra');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (ordenId) => {
    setExpandedOrden(expandedOrden === ordenId ? null : ordenId);
  };

  const getEstadoBadgeClass = (estado) => {
    const estadoMap = {
      'pendiente': 'badge-pendiente',
      'emitida': 'badge-emitida',
      'recibida': 'badge-recibida',
      'facturada': 'badge-facturada',
      'cancelada': 'badge-cancelada'
    };
    return estadoMap[estado] || 'badge-default';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL');
  };

  // Calcular estadísticas por estado
  const calcularEstadisticasPorEstado = () => {
    const estados = {
      pendiente: { label: 'Pendiente', count: 0, total: 0, color: '#f59e0b', bgColor: '#fef3c7' },
      emitida: { label: 'Emitida', count: 0, total: 0, color: '#3b82f6', bgColor: '#dbeafe' },
      recibida: { label: 'Recibida', count: 0, total: 0, color: '#10b981', bgColor: '#d1fae5' },
      facturada: { label: 'Facturada', count: 0, total: 0, color: '#69B32D', bgColor: '#e0e7ff' },
      cancelada: { label: 'Cancelada', count: 0, total: 0, color: '#ef4444', bgColor: '#fee2e2' }
    };

    ordenesFiltradas.forEach(orden => {
      if (estados[orden.estado]) {
        estados[orden.estado].count++;
        estados[orden.estado].total += parseFloat(orden.total_oc || 0);
      }
    });

    return estados;
  };

  // Obtener opciones únicas para los filtros
  const obtenerRecursosUnicos = () => {
    const recursosSet = new Set();
    ordenes.forEach(orden => {
      if (orden.detalles && orden.detalles.length > 0) {
        orden.detalles.forEach(detalle => {
          if (detalle.recurso_data?.nombre) {
            recursosSet.add(JSON.stringify({
              id: detalle.recurso_data.id,
              nombre: detalle.recurso_data.nombre
            }));
          }
        });
      }
    });
    return Array.from(recursosSet).map(r => JSON.parse(r)).sort((a, b) => a.nombre.localeCompare(b.nombre));
  };

  const obtenerProveedoresUnicos = () => {
    const proveedoresSet = new Set();
    ordenes.forEach(orden => {
      if (orden.proveedor_nombre) {
        proveedoresSet.add(JSON.stringify({
          id: orden.proveedor,
          nombre: orden.proveedor_nombre
        }));
      }
    });
    return Array.from(proveedoresSet).map(p => JSON.parse(p)).sort((a, b) => a.nombre.localeCompare(b.nombre));
  };

  const obtenerProyectosUnicos = () => {
    const proyectosSet = new Set();
    ordenes.forEach(orden => {
      if (orden.proyecto_nombre) {
        proyectosSet.add(JSON.stringify({
          id: orden.proyecto,
          nombre: orden.proyecto_nombre
        }));
      }
    });
    return Array.from(proyectosSet).map(p => JSON.parse(p)).sort((a, b) => a.nombre.localeCompare(b.nombre));
  };

  // Filtrar órdenes
  const ordenesFiltradas = ordenes.filter(orden => {
    // Filtro por número de OC
    if (filtroNumeroOC && !orden.numero_oc.toLowerCase().includes(filtroNumeroOC.toLowerCase())) {
      return false;
    }

    // Filtro por proveedor
    if (filtroProveedor && orden.proveedor !== parseInt(filtroProveedor)) {
      return false;
    }

    // Filtro por proyecto
    if (filtroProyecto && orden.proyecto !== parseInt(filtroProyecto)) {
      return false;
    }

    // Filtro por recurso (buscar en los detalles)
    if (filtroRecurso) {
      const tieneRecurso = orden.detalles?.some(
        detalle => detalle.recurso_data?.id === parseInt(filtroRecurso)
      );
      if (!tieneRecurso) {
        return false;
      }
    }

    return true;
  });

  const limpiarFiltros = () => {
    setFiltroRecurso('');
    setFiltroProveedor('');
    setFiltroProyecto('');
    setFiltroNumeroOC('');
  };

  const handleEditarOrdenCompra = (orden) => {
    // Mostrar el modal en modo edición
    setOrdenCompraParaVer(orden);
    setShowVerEditarOC(true);
  };

  const handleEliminarOrdenCompra = async (orden) => {
    if (!window.confirm(`¿Estás seguro de eliminar la Orden de Compra ${orden.numero_oc}?`)) {
      return;
    }

    try {
      await ordenCompraService.delete(orden.id);
      toast.success('Orden de compra eliminada correctamente');
      cargarOrdenes(); // Recargar la lista
    } catch (error) {
      console.error('Error al eliminar orden de compra:', error);
      toast.error('Error al eliminar la orden de compra');
    }
  };

  const handleRecepcionarCompra = async (orden) => {
    try {
      // Cargar la orden completa con todos sus detalles
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/content\/?$/i, '/onebase');
      const response = await api.get(`${API_BASE_URL}/ordenes-compra/${orden.id}/`);

      console.log('Orden completa cargada:', response.data);

      setOrdenCompraParaRecepcionar(response.data);
      setShowRecepcionModal(true);
    } catch (error) {
      console.error('Error al cargar orden de compra completa:', error);
      toast.error('Error al cargar los detalles de la orden de compra');
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="ordenes-compra-page">
      <div className="page-header">
        <h1>📋 Órdenes de Compra</h1>
      </div>

      {/* Cards de estados */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        {Object.entries(calcularEstadisticasPorEstado()).map(([estado, datos]) => (
          <div
            key={estado}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1rem',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: `3px solid ${datos.color}`,
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '0.5rem'
            }}>
              <span style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {datos.label}
              </span>
              <span style={{
                backgroundColor: datos.bgColor,
                color: datos.color,
                padding: '0.25rem 0.75rem',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: 700
              }}>
                {datos.count}
              </span>
            </div>
            <div style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: datos.color,
              marginBottom: '0.15rem'
            }}>
              {formatCurrency(datos.total)}
            </div>
            <div style={{
              fontSize: '0.75rem',
              color: '#9ca3af',
              fontWeight: 500
            }}>
              Total acumulado
            </div>
          </div>
        ))}

        {/* Card de Total General */}
        <div
          style={{
            background: 'linear-gradient(135deg, #102826 0%, #69B32D 100%)',
            borderRadius: '12px',
            padding: '1rem',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
            border: '3px solid #69B32D',
            transition: 'all 0.3s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '0.5rem'
          }}>
            <span style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'white',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Total General
            </span>
            <span style={{
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              color: 'white',
              padding: '0.25rem 0.75rem',
              borderRadius: '20px',
              fontSize: '0.875rem',
              fontWeight: 700
            }}>
              {ordenesFiltradas.length}
            </span>
          </div>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: 'white',
            marginBottom: '0.15rem'
          }}>
            {formatCurrency(ordenesFiltradas.reduce((sum, oc) => sum + parseFloat(oc.total_oc || 0), 0))}
          </div>
          <div style={{
            fontSize: '0.75rem',
            color: 'rgba(255, 255, 255, 0.8)',
            fontWeight: 500
          }}>
            Todas las órdenes
          </div>
        </div>
      </div>

      {/* Sección de filtros */}
      <div className="filtros-section">
        <div className="filtros-grid">
          <div className="filtro-item">
            <label htmlFor="filtro-numero-oc">N° OC</label>
            <input
              id="filtro-numero-oc"
              type="text"
              placeholder="Buscar por número..."
              value={filtroNumeroOC}
              onChange={(e) => setFiltroNumeroOC(e.target.value)}
              className="filtro-input"
            />
          </div>

          <div className="filtro-item">
            <label htmlFor="filtro-recurso">Recurso</label>
            <select
              id="filtro-recurso"
              value={filtroRecurso}
              onChange={(e) => setFiltroRecurso(e.target.value)}
              className="filtro-select"
            >
              <option value="">Todos los recursos</option>
              {obtenerRecursosUnicos().map(recurso => (
                <option key={recurso.id} value={recurso.id}>
                  {recurso.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="filtro-item">
            <label htmlFor="filtro-proveedor">Proveedor</label>
            <select
              id="filtro-proveedor"
              value={filtroProveedor}
              onChange={(e) => setFiltroProveedor(e.target.value)}
              className="filtro-select"
            >
              <option value="">Todos los proveedores</option>
              {obtenerProveedoresUnicos().map(proveedor => (
                <option key={proveedor.id} value={proveedor.id}>
                  {proveedor.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="filtro-item">
            <label htmlFor="filtro-proyecto">Proyecto</label>
            <select
              id="filtro-proyecto"
              value={filtroProyecto}
              onChange={(e) => setFiltroProyecto(e.target.value)}
              className="filtro-select"
            >
              <option value="">Todos los proyectos</option>
              {obtenerProyectosUnicos().map(proyecto => (
                <option key={proyecto.id} value={proyecto.id}>
                  {proyecto.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="filtro-item filtro-actions">
            <button
              onClick={limpiarFiltros}
              className="btn-limpiar-filtros"
              disabled={!filtroRecurso && !filtroProveedor && !filtroProyecto && !filtroNumeroOC}
            >
              🗑️ Limpiar filtros
            </button>
          </div>
        </div>
      </div>

      <div className="ordenes-table-container">
        <table className="ordenes-table">
          <thead>
            <tr>
              <th>N° OC</th>
              <th>Proyecto</th>
              <th>Proveedor</th>
              <th>Estado</th>
              <th>Tipo Doc.</th>
              <th>Fecha Emisión</th>
              <th>Total</th>
              <th>Items</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {ordenesFiltradas.length === 0 ? (
              <tr>
                <td colSpan="9" className="empty-row">
                  {ordenes.length === 0
                    ? 'No hay órdenes de compra registradas'
                    : 'No hay órdenes que coincidan con los filtros seleccionados'
                  }
                </td>
              </tr>
            ) : (
              ordenesFiltradas.map((orden) => (
                <React.Fragment key={orden.id}>
                  <tr className="orden-row">
                    <td className="numero-oc">
                      <strong>{orden.numero_oc}</strong>
                    </td>
                    <td>{orden.proyecto_nombre}</td>
                    <td>{orden.proveedor_nombre || '-'}</td>
                    <td>
                      <span className={`badge-estado ${getEstadoBadgeClass(orden.estado)}`}>
                        {orden.estado}
                      </span>
                    </td>
                    <td>
                      <span className="tipo-documento">
                        {orden.tipo_documento || '-'}
                      </span>
                    </td>
                    <td>{formatDate(orden.fecha_emision)}</td>
                    <td className="total-oc">
                      <strong>{formatCurrency(orden.total_oc || 0)}</strong>
                    </td>
                    <td>
                      <span className="items-count">{orden.numero_items || 0}</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          className="btn-icon-action"
                          onClick={() => handleEditarOrdenCompra(orden)}
                          title="Editar Orden"
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#2196F3',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#1976D2';
                            e.currentTarget.style.transform = 'scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#2196F3';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <FiEdit size={18} />
                        </button>
                        <button
                          className="btn-icon-action"
                          onClick={() => handleRecepcionarCompra(orden)}
                          title={orden.estado === 'recibida' ? 'Orden ya recibida completamente' : 'Recepcionar Compra'}
                          disabled={orden.estado === 'recibida'}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: orden.estado === 'recibida' ? 'not-allowed' : 'pointer',
                            color: orden.estado === 'recibida' ? '#ccc' : '#69B32D',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'all 0.2s',
                            opacity: orden.estado === 'recibida' ? 0.5 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (orden.estado !== 'recibida') {
                              e.currentTarget.style.color = '#5a9624';
                              e.currentTarget.style.transform = 'scale(1.1)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (orden.estado !== 'recibida') {
                              e.currentTarget.style.color = '#69B32D';
                              e.currentTarget.style.transform = 'scale(1)';
                            }
                          }}
                        >
                          <FiPackage size={18} />
                        </button>
                        <button
                          className="btn-icon-action danger"
                          onClick={() => handleEliminarOrdenCompra(orden)}
                          title="Eliminar Orden"
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#f44336',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#d32f2f';
                            e.currentTarget.style.transform = 'scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#f44336';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <FiTrash2 size={18} />
                        </button>
                        <button
                          className="btn-expand"
                          onClick={() => toggleExpand(orden.id)}
                          title={expandedOrden === orden.id ? "Ocultar detalles" : "Ver detalles"}
                        >
                          {expandedOrden === orden.id ? '▼' : '▶'}
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Fila expandida con detalles */}
                  {expandedOrden === orden.id && (
                    <tr className="detalles-row">
                      <td colSpan="9">
                        <div className="detalles-container">
                          <div className="detalles-header">
                            <h4>Detalles de la Orden</h4>
                            {orden.descripcion && (
                              <p className="orden-descripcion">
                                <strong>Descripción:</strong> {orden.descripcion}
                              </p>
                            )}
                          </div>

                          {orden.detalles && orden.detalles.length > 0 ? (
                            <table className="detalles-table" style={{
                              width: '100%',
                              borderCollapse: 'collapse',
                              backgroundColor: 'white',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                              borderRadius: '12px',
                              overflow: 'hidden'
                            }}>
                              <thead>
                                <tr style={{
                                  background: 'linear-gradient(135deg, #102826 0%, #69B32D 100%)',
                                  color: 'white'
                                }}>
                                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, fontSize: '0.95rem' }}>Recurso</th>
                                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, fontSize: '0.95rem' }}>Descripción</th>
                                  <th style={{ padding: '16px', textAlign: 'center', fontWeight: 600, fontSize: '0.95rem' }}>Cantidad</th>
                                  <th style={{ padding: '16px', textAlign: 'center', fontWeight: 600, fontSize: '0.95rem' }}>Unidad</th>
                                  <th style={{ padding: '16px', textAlign: 'right', fontWeight: 600, fontSize: '0.95rem' }}>Precio Unit.</th>
                                  <th style={{ padding: '16px', textAlign: 'right', fontWeight: 600, fontSize: '0.95rem' }}>Subtotal</th>
                                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, fontSize: '0.95rem' }}>Bodega</th>
                                </tr>
                              </thead>
                              <tbody>
                                {orden.detalles.map((detalle, idx) => (
                                  <tr
                                    key={detalle.id || idx}
                                    style={{
                                      borderBottom: '1px solid #e5e7eb',
                                      transition: 'background-color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                  >
                                    <td style={{ padding: '14px', fontSize: '0.9rem' }}>
                                      <div style={{ fontWeight: 600, color: '#1f2937' }}>
                                        {detalle.recurso_data?.nombre || '-'}
                                      </div>
                                      {detalle.recurso_data?.tiene_stock && (
                                        <span
                                          style={{
                                            marginLeft: '0.5rem',
                                            fontSize: '0.75rem',
                                            padding: '0.125rem 0.5rem',
                                            backgroundColor: '#dbeafe',
                                            color: '#1e40af',
                                            borderRadius: '12px'
                                          }}
                                        >
                                          📦 Stock
                                        </span>
                                      )}
                                    </td>
                                    <td style={{ padding: '14px', fontSize: '0.9rem', color: '#374151' }}>
                                      <div>{detalle.descripcion}</div>
                                      {detalle.recurso_data?.descripcion && (
                                        <small style={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                                          {detalle.recurso_data.descripcion}
                                        </small>
                                      )}
                                    </td>
                                    <td style={{ padding: '14px', textAlign: 'center', fontSize: '0.9rem', color: '#374151' }}>
                                      {parseFloat(detalle.cantidad).toFixed(2)}
                                    </td>
                                    <td style={{ padding: '14px', textAlign: 'center', fontSize: '0.9rem', color: '#374151' }}>
                                      {detalle.unidad}
                                    </td>
                                    <td style={{ padding: '14px', textAlign: 'right', fontSize: '0.9rem', color: '#374151' }}>
                                      {formatCurrency(detalle.precio_unitario)}
                                    </td>
                                    <td style={{
                                      padding: '14px',
                                      textAlign: 'right',
                                      fontSize: '0.95rem',
                                      fontWeight: 700,
                                      color: '#ef4444'
                                    }}>
                                      {formatCurrency(detalle.subtotal)}
                                    </td>
                                    <td style={{ padding: '14px', fontSize: '0.9rem', color: '#374151' }}>
                                      {detalle.bodega_nombre || '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr style={{
                                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                  fontWeight: 700,
                                  borderTop: '3px solid #69B32D'
                                }}>
                                  <td colSpan="5" style={{
                                    padding: '16px',
                                    textAlign: 'right',
                                    fontSize: '1.1rem',
                                    color: '#1f2937'
                                  }}>
                                    TOTAL OC:
                                  </td>
                                  <td style={{
                                    padding: '16px',
                                    textAlign: 'right',
                                    color: '#ef4444',
                                    fontSize: '1.2rem',
                                    fontWeight: 700
                                  }}>
                                    {formatCurrency(orden.total_oc || 0)}
                                  </td>
                                  <td></td>
                                </tr>
                              </tfoot>
                            </table>
                          ) : (
                            <p className="no-detalles">Esta orden no tiene detalles registrados</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal para ver/editar orden de compra */}
      {showVerEditarOC && ordenCompraParaVer && (
        <VerEditarOrdenCompraModal
          ordenCompra={ordenCompraParaVer}
          onClose={() => {
            setShowVerEditarOC(false);
            setOrdenCompraParaVer(null);
          }}
          onUpdated={() => {
            cargarOrdenes();
          }}
          readOnly={false}
        />
      )}

      {/* Modal para recepcionar compra */}
      {showRecepcionModal && ordenCompraParaRecepcionar && (
        <RecepcionarCompraModal
          show={showRecepcionModal}
          ordenCompra={ordenCompraParaRecepcionar}
          onClose={() => {
            setShowRecepcionModal(false);
            setOrdenCompraParaRecepcionar(null);
          }}
          onSuccess={() => {
            cargarOrdenes();
          }}
        />
      )}
    </div>
  );
};

export default OrdenesCompraPage;
